/**
 * @file 企业版联系表单弹窗
 */

import React, {type FC, useMemo} from 'react';
import {Modal, Form, Input, Select, Button} from 'antd';

import RequiredSvg from '@/assets/billing/required.svg';
import styles from './index.module.less';

export interface IEnterpriseContactValues {
    industry: string;
    contactName: string;
    contactInfo: string;
    companyName?: string;
    scenario?: string;
}

interface IEnterpriseContactModalProps {
    open: boolean;
    submitting?: boolean;
    onCancel: () => void;
    onSubmit: (values: IEnterpriseContactValues) => Promise<void> | void;
}

const industryOptions = [
    {label: '制造业', value: '制造业'},
    {label: '零售电商', value: '零售电商'},
    {label: '物流运输', value: '物流运输'},
    {label: '能源化工', value: '能源化工'},
    {label: '金融服务', value: '金融服务'},
    {label: '政府与公共服务', value: '政府与公共服务'}
];

const contactPattern = /^(1\d{10}|[\w.-]+@[\w.-]+\.[A-Za-z]{2,}|[A-Za-z][A-Za-z0-9_-]{5,19})$/;

const renderRequiredLabel = (label: string) => (
    <span>
        {label}
        <RequiredSvg style={{marginLeft: 4}} />
    </span>
);

const EnterpriseContactModal: FC<IEnterpriseContactModalProps> = props => {
    const [form] = Form.useForm();

    const formValues = Form.useWatch([], form);

    const canSubmit = useMemo(
        () => Boolean(formValues?.industry && formValues?.contactName?.trim() && formValues?.contactInfo?.trim()),
        [formValues]
    );

    const handleClose = () => {
        form.resetFields();
        props.onCancel();
    };

    const handleFinish = async (values: IEnterpriseContactValues) => {
        await props.onSubmit({
            ...values,
            contactName: values.contactName.trim(),
            contactInfo: values.contactInfo.trim(),
            companyName: values.companyName?.trim(),
            scenario: values.scenario?.trim()
        });
        form.resetFields();
    };

    return (
        <Modal
            centered
            destroyOnHidden
            width={600}
            footer={null}
            open={props.open}
            className={styles.contactModal}
            title="填写以下信息，我们将尽快与您联系"
            onCancel={handleClose}
        >
            <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                onFinish={handleFinish}
            >
                <Form.Item
                    label={renderRequiredLabel('您所属的行业')}
                    name="industry"
                    required
                    rules={[{required: true, message: '请选择您所在的行业'}]}
                >
                    <Select
                        placeholder="请选择您所在的行业"
                        options={industryOptions}
                    />
                </Form.Item>

                <Form.Item
                    label={renderRequiredLabel('联系人姓名')}
                    name="contactName"
                    required
                    rules={[
                        {required: true, message: '请输入联系人的姓名'},
                        {max: 30, message: '联系人姓名不能超过30个字符'}
                    ]}
                >
                    <Input placeholder="请输入联系人的姓名" maxLength={30} />
                </Form.Item>

                <Form.Item
                    label={renderRequiredLabel('联系方式')}
                    name="contactInfo"
                    required
                    rules={[
                        {required: true, message: '请输入手机号、微信或邮箱'},
                        {pattern: contactPattern, message: '请输入有效的手机号、微信号或邮箱'}
                    ]}
                >
                    <Input placeholder="请输入手机号、微信或邮箱" maxLength={40} />
                </Form.Item>

                <Form.Item label="企业名称" name="companyName" required>
                    <Input placeholder="请输入企业名称" maxLength={60} />
                </Form.Item>

                <Form.Item label="计划应用场景" name="scenario" required>
                    <Input placeholder="请输入您的使用场景，例如：生产排程、需求预测、路径优化" maxLength={120} />
                </Form.Item>

                <Button
                    block
                    type="primary"
                    htmlType="submit"
                    loading={props.submitting}
                    disabled={!canSubmit}
                    style={{height: 40}}
                >
                    立即提交
                </Button>
            </Form>
        </Modal>
    );
};

export default EnterpriseContactModal;
